// src/features/clients/pages/BirthdayPanel.tsx
// ============================================================================
// PANEL DE CUMPLEAÑOS DE CLIENTES
// ----------------------------------------------------------------------------
// Re-cableado a lealify-base (Inc12b). CERO Firebase / esapi / WAHA-navegador.
//
// CAMBIO DE PARADIGMA:
//   - La LISTA viene de birthdaysApi.listUpcoming() (ya partida hoy/próximos,
//     ya excluye dados de baja de marketing, ya ordenada por el backend).
//   - El ENVÍO es SERVER-SIDE: birthdaysApi.send(contactId, force?) dispara el
//     broker WhatsApp de base (dedup anual + modo shadow en el backend). El
//     navegador ya NO arma ni manda el mensaje.
//   - La CONFIG de envío automático + resumen diario vive en
//     birthdaysApi.get/saveSettings (no en businesses/{id}.birthdayAutoMessage).
//   - El TEMPLATE vive en settings.template (editor inline; placeholders
//     @nombre @marca @puntos). Sin storageKey/localStorage ni sync a n8n.
//   - El ESTADO de conexión WhatsApp viene de whatsappApi.status().
//   - El opt-out de marketing usa clientsApi.setMarketingOptOut().
//
// TODO: imagen de cumpleaños diferida (base envía solo texto por ahora, igual
//       que campañas/mensajes en este re-cableo).
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { hasModule } from '../../../utils/modulePermissions';
import Layout from '../../../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import {
  Cake, Gift, Loader2, Settings,
  Users, DollarSign, Star, BellOff, X, UserMinus,
  Zap, ToggleLeft, ToggleRight, Bell, Wifi, WifiOff, Clock,
  Send, Check, Save, Image as ImageIcon, AlertTriangle, RefreshCw, History,
} from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import { toast } from 'react-hot-toast';
import { birthdaysApi, failHelp } from '../../../api/birthdaysApi';
import type { BirthdayContact, BirthdaySettings, EnvioPreview } from '../../../api/birthdaysApi';
import { whatsappApi } from '../../../api/whatsappApi';
import { clientsApi } from '../../../api/clientsApi';
import ImageUpload from '../../../components/common/ImageUpload';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import PaginationControls from '../../../components/common/PaginationControls';
import { formatBirthday } from '../../../utils/dates';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
import ExportExcelButton from '../../../components/common/ExportExcelButton';
import ExportModal, { ExportField } from '../../../components/common/ExportModal';
import * as XLSX from 'xlsx';
import type { BirthdayHistoryRow } from '../../../api/birthdaysApi';

// --- INTERFACES Y CONSTANTES ---
interface DailyUpcomingBirthdays {
  dateKey: string;
  displayDate: string;
  birthdays: BirthdayContact[];
  count: number;
}

type SendState = 'sending' | 'sent' | 'error';

const DAYS_TO_FETCH = 30;
const ITEMS_PER_PAGE = 20;

// ── Chip de pronóstico del envío automático (31-jul-2026) ────────────────────
// Traduce el preview del gate anti-ban (`contact.envio`, backend ?preview=1) al
// lenguaje del DUEÑO: nunca expone internos (kicks, reincidencia, edad de sesión).
// Solo se pinta con el envío automático encendido y si aún no se le felicitó.
const fmtHasta = (s: string | null | undefined) =>
  s ? new Date(`${s}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : null;

function envioChipDe(e: EnvioPreview): { txt: string; cls: string; tip?: string } | null {
  if (e.estado === 'sale') {
    return { txt: '✓ Se felicitará sola', cls: 'bg-green-500/10 text-green-700 dark:text-green-300',
      tip: e.via === 'conversacion' ? 'Les has conversado por WhatsApp' : 'Cliente con actividad reciente' };
  }
  if (e.estado !== 'frenado') return null;
  const M: Record<string, { txt: string; cls: string; tip?: string }> = {
    joven_frio: { txt: `⏸ En pausa${fmtHasta(e.hasta) ? ` hasta el ${fmtHasta(e.hasta)}` : ''}`,
      cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
      tip: 'Tu número de WhatsApp está en recuperación; ese día se reanudan los envíos a quien no te ha escrito' },
    observacion_463_frio: { txt: `⏸ En pausa${fmtHasta(e.hasta) ? ` hasta el ${fmtHasta(e.hasta)}` : ''}`,
      cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
      tip: 'Tu número de WhatsApp está en recuperación' },
    dormido_frio: { txt: 'No saldrá sola — sin visita reciente',
      cls: 'bg-surface-2 text-content-3',
      tip: 'Protección del número: se reactiva sola con su próxima visita o mensaje. Puedes enviarla tú desde el botón (te pedirá confirmar).' },
    nunca_cliente: { txt: 'No saldrá sola — sin visitas registradas',
      cls: 'bg-surface-2 text-content-3',
      tip: 'No registra visitas ni mensajes; escribirle en frío arriesga tu número. Puedes enviarla tú desde el botón (te pedirá confirmar).' },
    reincidente_frio: { txt: 'No saldrá sola — solo clientes con relación',
      cls: 'bg-surface-2 text-content-3',
      tip: 'Protección del número: solo salen envíos a clientes con visita o conversación' },
    sin_whatsapp: { txt: 'Sin WhatsApp', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Este número no está registrado en WhatsApp' },
    sesion_caida: { txt: 'WhatsApp desconectado', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Reconecta tu WhatsApp para que salgan las felicitaciones' },
    bloqueo_463: { txt: 'Envíos en pausa — protección del número', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
    semaforo_rojo: { txt: 'Envíos en pausa — protección del número', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
  };
  return M[e.motivo || ''] || null;
}

// ── Historial (7-sep-2026) ───────────────────────────────────────────────────
// Qué felicitaciones salieron y cuáles no (y por qué) en los últimos N días. Lo pidió
// Club W («mi gerente quiere un control de a quiénes se les ha mandado»). Solo lectura:
// sin reenviar ni dar de baja desde aquí — para eso están Hoy y Próximos.
const HISTORY_RANGES = [30, 60, 90] as const;
type HistoryDays = typeof HISTORY_RANGES[number];
const HISTORY_PER_PAGE = 10;

// Mismo vocabulario que envioChipDe, pero en pasado: aquí el envío YA no salió.
function historialChipDe(r: BirthdayHistoryRow): { txt: string; cls: string; tip?: string } {
  if (r.sendStatus === 'sent') {
    return { txt: '✓ Enviada', cls: 'bg-green-500/10 text-green-700 dark:text-green-300',
      tip: r.channel === 'email' ? 'Enviada por correo' : r.channel === 'both' ? 'Enviada por WhatsApp y correo' : 'Enviada por WhatsApp' };
  }
  if (r.sendStatus === 'shadow') {
    return { txt: 'Simulada', cls: 'bg-surface-2 text-content-3', tip: 'Se registró sin enviar (modo de prueba)' };
  }
  if (r.sendStatus === 'failed') {
    return { txt: `✗ ${r.failReason || 'No se envió'}`, cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: r.failDetail ? failHelp(r.failDetail) : undefined };
  }
  const M: Record<string, { txt: string; cls: string; tip?: string }> = {
    joven_frio: { txt: 'Sin enviar · número recién vinculado', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
      tip: 'Tu WhatsApp llevaba pocos días vinculado y esta persona nunca te había escrito; para proteger el número no se le escribió' },
    observacion_463_frio: { txt: 'Sin enviar · número en recuperación', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
      tip: 'Tu número de WhatsApp estaba en recuperación ese día' },
    sesion_caida: { txt: 'Sin enviar · WhatsApp desconectado', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Ese día tu WhatsApp no estaba vinculado' },
    sin_whatsapp: { txt: 'Sin enviar · sin WhatsApp', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Este número no está registrado en WhatsApp' },
    dormido_frio: { txt: 'Sin enviar · sin visita reciente', cls: 'bg-surface-2 text-content-3',
      tip: 'Protección del número: no se escribe en frío a quien no ha visitado ni conversado' },
    nunca_cliente: { txt: 'Sin enviar · sin visitas registradas', cls: 'bg-surface-2 text-content-3',
      tip: 'No registraba visitas ni mensajes; escribirle en frío arriesga tu número' },
    reincidente_frio: { txt: 'Sin enviar · solo clientes con relación', cls: 'bg-surface-2 text-content-3',
      tip: 'Protección del número: solo salen envíos a clientes con visita o conversación' },
    bloqueo_463: { txt: 'Sin enviar · envíos en pausa', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Los envíos estaban en pausa por protección del número' },
    semaforo_rojo: { txt: 'Sin enviar · envíos en pausa', cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
      tip: 'Los envíos estaban en pausa por protección del número' },
    sin_intento: { txt: 'Sin enviar · no se intentó', cls: 'bg-surface-2 text-content-3',
      tip: 'Ese día la felicitación automática estaba apagada o el cliente no entraba en el envío' },
  };
  return M[r.motivo || 'sin_intento'] || M.sin_intento;
}

const HistorialChip: React.FC<{ row: BirthdayHistoryRow }> = ({ row }) => {
  const c = historialChipDe(row);
  return (
    <span title={c.tip} className={`inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${c.cls}`}>
      {c.txt}
    </span>
  );
};

const HISTORY_EXPORT_FIELDS: ExportField[] = [
  { key: 'cliente', header: 'Cliente', label: 'Cliente', alwaysIncluded: true, columnWidth: 30 },
  { key: 'estado', header: 'Estado', label: 'Estado', alwaysIncluded: true, columnWidth: 34 },
  { key: 'cumple', header: 'Cumpleaños', label: 'Fecha de cumpleaños', columnWidth: 14 },
  { key: 'envio', header: 'Fecha de envío', label: 'Fecha y hora de envío', columnWidth: 18 },
  { key: 'canal', header: 'Canal', label: 'Canal (WhatsApp / correo)', columnWidth: 10 },
  { key: 'telefono', header: 'Teléfono', label: 'Teléfono', columnWidth: 16 },
  { key: 'motivo', header: 'Motivo', label: 'Motivo si no se envió', columnWidth: 40 },
];

const EnvioChip: React.FC<{ visitor: BirthdayContact; autoEnabled: boolean }> = ({ visitor, autoEnabled }) => {
  if (!autoEnabled || visitor.greeted || !visitor.envio) return null;
  const c = envioChipDe(visitor.envio);
  if (!c) return null;
  return (
    <span title={c.tip} className={`inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${c.cls}`}>
      {c.txt}
    </span>
  );
};
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DEFAULT_BIRTHDAY_MESSAGE = '¡Feliz cumpleaños @nombre! 🎂 Queremos desearte un día maravilloso. De parte de @negocio';

// --- COMPONENT ---

const BirthdayPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const contentTopRef = useRef<HTMLDivElement>(null);

  // ── Lista (ya partida por el backend) ───────────────────────────────────
  const [todaysBirthdays, setTodaysBirthdays] = useState<BirthdayContact[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<BirthdayContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Opt-out de marketing ────────────────────────────────────────────────
  const [optingOutClientId, setOptingOutClientId] = useState<string | null>(null);
  const [optOutCandidate, setOptOutCandidate] = useState<BirthdayContact | null>(null);

  // ── Envío server-side: estado por-contacto ──────────────────────────────
  const [sendState, setSendState] = useState<Record<string, SendState>>({});
  // Chip de fallo desplegado (uno a la vez): al tocarlo muestra la explicación en
  // lenguaje llano y, plegado dentro, el detalle técnico crudo.
  const [failOpenId, setFailOpenId] = useState<string | null>(null);

  // ── Historial (7-sep-2026) ──────────────────────────────────────────────
  // Pestañas Próximos | Historial (7-sep-2026): pegado debajo de Próximos, el historial se leía
  // como «más fechas». Patrón de pestañas de Ajustes/Mi Negocio.
  const [activeTab, setActiveTab] = useState<'proximos' | 'historial'>('proximos');
  const [historyDays, setHistoryDays] = useState<HistoryDays>(60);
  const [historyRows, setHistoryRows] = useState<BirthdayHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [showHistoryExport, setShowHistoryExport] = useState(false);

  // ── Settings (config automática + template) ─────────────────────────────
  const [settings, setSettings] = useState<BirthdaySettings | null>(null);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [notifyOwner, setNotifyOwner] = useState(true);
  const [autoLoading, setAutoLoading] = useState(true);
  const [autoSaving, setAutoSaving] = useState(false);

  // ── Regalo de puntos de cumpleaños (28-ago-2026) ────────────────────────
  // Abona saldo REAL del negocio, así que el monto se edita y se GUARDA a propósito
  // (no se auto-guarda al teclear): un dedazo aquí son puntos regalados a cada
  // cumpleañero del año. El backend topa en 500.
  const [giftEnabled, setGiftEnabled] = useState(false);
  const [giftPointsDraft, setGiftPointsDraft] = useState('20');
  const [savingGift, setSavingGift] = useState(false);

  // ── Editor inline de template ───────────────────────────────────────────
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState(DEFAULT_BIRTHDAY_MESSAGE);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // ── Estado de conexión WhatsApp (broker de base) ────────────────────────
  const [waConfigured, setWaConfigured] = useState<boolean | null>(null);
  const [waConnected, setWaConnected] = useState<boolean>(false);

  const hasProAccess = checkIsPro(currentUser?.subscription);
  // Un Empleado (birthdays 'operate') felicita y ve la lista; el envío automático, el regalo
  // de puntos, la imagen y la plantilla son configuración → solo con 'manage' (el backend
  // ya devolvía 403 en PUT /birthdays/settings; ahora tampoco se le ofrecen los controles).
  const canManage = hasModule(currentUser, 'birthdays', 'manage');
  const showPaywall = !hasProAccess;
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  const { localDay, localMonthIndex } = useMemo(() => {
    const n = new Date();
    return { localDay: n.getDate(), localMonthIndex: n.getMonth() };
  }, []);

  const currentMonthName = MONTH_NAMES[localMonthIndex];

  // ============================================================================
  // CARGAR LISTA DE CUMPLEAÑOS (server-side, ya partida hoy/próximos)
  // ============================================================================
  const fetchBirthdays = useCallback(async () => {
    if (!hasProAccess) { setLoading(false); return; }

    setLoading(true);
    setError(null);
    try {
      const { today, upcoming } = await birthdaysApi.listUpcoming(DAYS_TO_FETCH);
      setTodaysBirthdays(today);
      setUpcomingBirthdays(upcoming);
      setCurrentPage(1);
    } catch (err: any) {
      setError(`Error al obtener cumpleaños: ${err?.message || 'desconocido'}`);
      setTodaysBirthdays([]);
      setUpcomingBirthdays([]);
    } finally {
      setLoading(false);
    }
  }, [hasProAccess]);

  useEffect(() => {
    fetchBirthdays();
  }, [fetchBirthdays]);

  // ============================================================================
  // CARGAR SETTINGS (config automática + template) + ESTADO WHATSAPP
  // ============================================================================
  useEffect(() => {
    if (!hasProAccess) { setAutoLoading(false); return; }

    let cancelled = false;

    const loadConfig = async () => {
      setAutoLoading(true);
      try {
        const { settings: s } = await birthdaysApi.getSettings();
        if (cancelled) return;
        setSettings(s);
        setAutoEnabled(s.autoMessageEnabled);
        setNotifyOwner(s.notifyOwner);
        setTemplateDraft(s.template || DEFAULT_BIRTHDAY_MESSAGE);
        setGiftEnabled(s.giftPointsEnabled);
        if (s.giftPoints > 0) setGiftPointsDraft(String(s.giftPoints));
      } catch (err) {
        console.error('Error cargando settings de cumpleaños:', err);
      } finally {
        if (!cancelled) setAutoLoading(false);
      }
    };

    const loadWhatsApp = async () => {
      try {
        const st = await whatsappApi.status();
        if (cancelled) return;
        setWaConfigured(st.configured);
        setWaConnected(st.connected === true);
      } catch {
        if (cancelled) return;
        setWaConfigured(false);
        setWaConnected(false);
      }
    };

    loadConfig();
    loadWhatsApp();

    return () => { cancelled = true; };
  }, [hasProAccess]);

  // ============================================================================
  // TOGGLES DE ENVÍO AUTOMÁTICO (server-side via saveSettings)
  // ============================================================================
  const handleToggleAutoEnabled = useCallback(async () => {
    if (autoSaving) return;
    const newValue = !autoEnabled;
    setAutoSaving(true);
    try {
      const s = await birthdaysApi.saveSettings({ autoMessageEnabled: newValue });
      setSettings(s);
      setAutoEnabled(s.autoMessageEnabled);
      toast.success(newValue ? 'Envío automático activado' : 'Envío automático desactivado');
    } catch (err) {
      console.error('Error toggling auto-birthday:', err);
      toast.error('Error al guardar configuración');
    } finally {
      setAutoSaving(false);
    }
  }, [autoEnabled, autoSaving]);

  // Imagen de felicitación: se adjunta a cada mensaje de cumpleaños (el backend hace sendImage
  // si hay image_url). Sube vía ImageUpload (api.lealify.com/upload) y persiste en settings.
  const handleSetImage = useCallback(async (url: string) => {
    const next = url && url.trim() ? url.trim() : null;
    // Evita guardar URLs a medio escribir; solo http(s) completas o limpiar.
    if (next && !/^https?:\/\//i.test(next)) return;
    if (next === (settings?.imageUrl ?? null)) return;
    try {
      const s = await birthdaysApi.saveSettings({ imageUrl: next });
      setSettings(s);
      toast.success(next ? 'Imagen de felicitación actualizada' : 'Imagen de felicitación quitada');
    } catch (err) {
      console.error('Error guardando imagen de cumpleaños:', err);
      toast.error('Error al guardar la imagen');
    }
  }, [settings]);

  const handleToggleNotifyOwner = useCallback(async () => {
    if (autoSaving) return;
    const newValue = !notifyOwner;
    setAutoSaving(true);
    try {
      const s = await birthdaysApi.saveSettings({ notifyOwner: newValue });
      setSettings(s);
      setNotifyOwner(s.notifyOwner);
      toast.success(newValue ? 'Recibirás resumen diario' : 'Resumen diario desactivado');
    } catch (err) {
      console.error('Error toggling notifyOwner:', err);
      toast.error('Error al guardar configuración');
    } finally {
      setAutoSaving(false);
    }
  }, [notifyOwner, autoSaving]);

  // ============================================================================
  // REGALO DE PUNTOS (server-side via saveSettings)
  // ============================================================================
  // Encender EXIGE un monto > 0: un regalo de 0 puntos manda un mensaje que promete
  // algo y no abona nada — peor que no tener la función.
  const handleToggleGift = useCallback(async () => {
    if (savingGift) return;
    const next = !giftEnabled;
    const pts = Math.min(500, Math.max(0, Math.trunc(Number(giftPointsDraft) || 0)));
    if (next && pts <= 0) return;
    setSavingGift(true);
    try {
      const s = await birthdaysApi.saveSettings({ giftPointsEnabled: next, giftPoints: pts });
      setSettings(s);
      setGiftEnabled(s.giftPointsEnabled);
      if (s.giftPoints > 0) setGiftPointsDraft(String(s.giftPoints));
    } catch (err) {
      console.error('Error cambiando el regalo de cumpleaños:', err);
    } finally {
      setSavingGift(false);
    }
  }, [giftEnabled, giftPointsDraft, savingGift]);

  const handleSaveGiftPoints = useCallback(async () => {
    if (savingGift) return;
    const pts = Math.min(500, Math.max(0, Math.trunc(Number(giftPointsDraft) || 0)));
    if (pts <= 0) { setGiftPointsDraft(String(settings?.giftPoints || 0)); return; }
    if (pts === (settings?.giftPoints ?? -1)) return;      // sin cambio real
    setSavingGift(true);
    try {
      const s = await birthdaysApi.saveSettings({ giftPoints: pts });
      setSettings(s);
      setGiftPointsDraft(String(s.giftPoints));
    } catch (err) {
      console.error('Error guardando los puntos de regalo:', err);
    } finally {
      setSavingGift(false);
    }
  }, [giftPointsDraft, settings, savingGift]);

  // ============================================================================
  // GUARDAR TEMPLATE (editor inline → settings.template)
  // ============================================================================
  const handleSaveTemplate = useCallback(async () => {
    if (savingTemplate) return;
    const text = templateDraft.trim();
    if (!text) { toast.error('El mensaje no puede estar vacío'); return; }
    setSavingTemplate(true);
    try {
      const s = await birthdaysApi.saveSettings({ template: text });
      setSettings(s);
      setTemplateDraft(s.template || text);
      toast.success('Mensaje guardado');
      setIsEditingTemplate(false);
    } catch (err) {
      console.error('Error guardando template de cumpleaños:', err);
      toast.error('Error al guardar el mensaje');
    } finally {
      setSavingTemplate(false);
    }
  }, [templateDraft, savingTemplate]);

  // ============================================================================
  // ENVIAR FELICITACIÓN (server-side, dedup anual + shadow en el backend)
  // ============================================================================
  const patchContact = useCallback((id: string, patch: Partial<BirthdayContact>) => {
    setTodaysBirthdays(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
    setUpcomingBirthdays(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  const markGreeted = useCallback((id: string) => {
    patchContact(id, { greeted: true, sendStatus: 'sent', failReason: null, failDetail: null });
  }, [patchContact]);

  const handleSend = useCallback(async (contact: BirthdayContact, force = false) => {
    if (!contact.id || sendState[contact.id] === 'sending') return;
    setSendState(prev => ({ ...prev, [contact.id]: 'sending' }));
    try {
      const res = await birthdaysApi.send(contact.id, force);
      if (res.ok) {
        setSendState(prev => ({ ...prev, [contact.id]: 'sent' }));
        markGreeted(contact.id);
        toast.success(res.shadow ? 'Felicitación registrada (modo prueba)' : '¡Felicitación enviada!');
      } else {
        setSendState(prev => ({ ...prev, [contact.id]: 'error' }));
        const nombre = contact.visitorName || 'El cliente';
        if (res.reason === 'already_greeted') {
          markGreeted(contact.id);
          toast('Ya felicitado este año', { icon: '🎂' });
        } else if (res.reason === 'invalid_phone') {
          // El teléfono guardado no puede existir (le faltan dígitos): ni se intentó.
          // Se conserva el detail del backend para que el desplegable diga cuántos tiene.
          patchContact(contact.id, {
            greeted: false, sendStatus: 'failed', failReason: 'Teléfono incompleto',
            failDetail: `whatsapp:tel-invalido ${String(contact.phoneNumber || '').replace(/\D/g, '').length} digitos`,
          });
          toast.error(`El teléfono de ${nombre} está incompleto — corrígelo en su ficha`);
        } else if (res.reason === 'no_whatsapp' || res.reason === 'no_phone') {
          // Confirmado por lookup: el número no está en WhatsApp. Reintentar no sirve —
          // hay que corregirle el teléfono en su ficha.
          patchContact(contact.id, { greeted: false, sendStatus: 'failed', failReason: 'Sin WhatsApp', failDetail: null });
          toast.error(`${nombre} no tiene WhatsApp en ese número`);
        } else if (res.reason === 'not_delivered') {
          // Falló, pero no pudimos confirmar si es del número o de la sesión.
          patchContact(contact.id, { greeted: false, sendStatus: 'failed', failReason: 'No se entregó', failDetail: null });
          toast.error('No se pudo entregar la felicitación');
        } else if (res.reason === 'wa_transient') {
          // El número SÍ existe: el fallo fue de la sesión → el envío automático lo reintenta.
          toast.error('WhatsApp falló en este momento; se reintenta en la próxima corrida');
        } else if (res.reason === 'timelock_463') {
          patchContact(contact.id, { greeted: false, sendStatus: 'failed', failReason: 'WhatsApp bloqueado', failDetail: null });
          toast.error('WhatsApp frenó los envíos de esta cuenta; se destraba en unos días');
        } else {
          // OJO: res.message trae el TEXTO de la felicitación, no un error → no mostrarlo.
          patchContact(contact.id, { greeted: false, sendStatus: 'failed', failReason: 'No se envió', failDetail: null });
          toast.error('No se pudo enviar la felicitación');
        }
      }
    } catch (err) {
      console.error('Error enviando felicitación:', err);
      setSendState(prev => ({ ...prev, [contact.id]: 'error' }));
      toast.error('Error al enviar la felicitación');
    }
  }, [sendState, markGreeted, patchContact]);

  // Botón "Felicitar" por fila (estados: enviando / felicitado / error / normal)
  const renderSendButton = (contact: BirthdayContact, size: 'sm' | 'md') => {
    const state = sendState[contact.id];
    const greeted = contact.greeted || state === 'sent';
    const sizeCls = size === 'md' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-xs';
    const iconCls = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

    if (state === 'sending') {
      return (
        <button
          disabled
          className={`inline-flex items-center ${sizeCls} font-medium rounded-md text-content-3 bg-canvas border border-line cursor-wait`}
        >
          <Loader2 className={`${iconCls} mr-1.5 animate-spin`} /> Enviando…
        </button>
      );
    }

    if (greeted) {
      return (
        <button
          onClick={() => handleSend(contact, true)}
          title="Reenviar felicitación"
          className={`inline-flex items-center ${sizeCls} font-medium rounded-md text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors`}
        >
          <Check className={`${iconCls} mr-1.5`} /> Felicitado
        </button>
      );
    }

    // El envío de este año FALLÓ (típicamente el número no está en WhatsApp). Queda fila en
    // birthday_sends para que el cron no se atore reintentándolo, pero NO es una felicitación:
    // se muestra el motivo y un reenvío manual con force (sin force el backend responde
    // 'already_greeted' por la fila existente).
    if (contact.sendStatus === 'failed' || state === 'error') {
      const label = contact.failReason || 'No se envió';
      const ayuda = failHelp(contact.failDetail);
      const abierto = failOpenId === contact.id;
      return (
        <div className="inline-flex flex-col items-start gap-1.5">
          <div className="inline-flex items-center gap-1.5">
            {/* El chip explica en cristiano al pasar el ratón y, al tocarlo, despliega
                la explicación completa (en móvil no hay hover). El volcado técnico de
                WAHA queda detrás de "Detalle técnico": sigue disponible para
                diagnóstico, pero deja de ser lo primero que ve el dueño del negocio. */}
            <button
              type="button"
              onClick={() => setFailOpenId(abierto ? null : contact.id)}
              title={ayuda}
              aria-expanded={abierto}
              className={`inline-flex items-center ${sizeCls} font-medium rounded-md text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors text-left`}
            >
              <AlertTriangle className={`${iconCls} mr-1.5 flex-shrink-0`} /> {label}
            </button>
            <button
              onClick={() => handleSend(contact, true)}
              title="Reintentar el envío"
              className={`inline-flex items-center justify-center p-1.5 rounded-md text-content-3 hover:text-content-2 bg-surface hover:bg-surface-2 border border-line transition-colors`}
            >
              <RefreshCw className={iconCls} />
            </button>
          </div>
          {abierto && (
            <div className="max-w-xs rounded-md border border-line bg-surface-2 px-3 py-2 text-[11px] leading-relaxed text-content-2">
              <p>{ayuda}</p>
              {contact.failDetail && (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-content-3 hover:text-content-2 select-none">
                    Detalle técnico
                  </summary>
                  <p className="mt-1 break-all font-mono text-[10px] text-content-3">
                    {contact.failDetail}
                  </p>
                </details>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        onClick={() => handleSend(contact, false)}
        className={`inline-flex items-center ${sizeCls} font-medium rounded-md text-white shadow-sm transition-colors bg-green-600 hover:bg-green-700`}
        title="Enviar felicitación por WhatsApp"
      >
        <Send className={`${iconCls} mr-1.5`} /> Felicitar
      </button>
    );
  };

  // ============================================================================
  // HISTORIAL (últimos N días) — carga por rango, paginación local
  // ============================================================================
  useEffect(() => {
    let alive = true;
    setHistoryLoading(true);
    birthdaysApi.listHistory(historyDays)
      .then(({ rows }) => { if (alive) { setHistoryRows(rows); setHistoryPage(1); } })
      .catch((e) => { console.error('[BirthdayPanel] historial:', e); if (alive) toast.error('No se pudo cargar el historial'); })
      .finally(() => { if (alive) setHistoryLoading(false); });
    return () => { alive = false; };
  }, [historyDays]);

  // «Sin enviar» = se intentó o el gate lo frenó (fallidos + joven_frio, sesion_caida…).
  // «Sin intento» = ese día no tocaba (automático apagado, antes de activarlo, opt-out): va
  // aparte para que el badge no grite «31 sin enviar» por cumpleaños de antes de la activación.
  const historyTotals = useMemo(() => {
    let enviadas = 0, sinEnviar = 0, sinIntento = 0;
    for (const r of historyRows) {
      if (r.sendStatus === 'sent' || r.sendStatus === 'shadow') enviadas++;
      else if (!r.sendStatus && r.motivo === 'sin_intento') sinIntento++;
      else sinEnviar++;
    }
    return { enviadas, sinEnviar, sinIntento };
  }, [historyRows]);

  const historyTotalPages = Math.max(1, Math.ceil(historyRows.length / HISTORY_PER_PAGE));
  const historyPageRows = useMemo(
    () => historyRows.slice((historyPage - 1) * HISTORY_PER_PAGE, historyPage * HISTORY_PER_PAGE),
    [historyRows, historyPage]);

  // Agrupado por fecha real del cumpleaños (más reciente primero), como Próximos.
  const historyGroups = useMemo(() => {
    const groups: { dateKey: string; displayDate: string; rows: BirthdayHistoryRow[] }[] = [];
    for (const r of historyPageRows) {
      let g = groups[groups.length - 1];
      if (!g || g.dateKey !== r.bdayDate) {
        const d = new Date(`${r.bdayDate}T12:00:00`);
        const label = d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
        g = { dateKey: r.bdayDate, displayDate: label.charAt(0).toUpperCase() + label.slice(1), rows: [] };
        groups.push(g);
      }
      g.rows.push(r);
    }
    return groups;
  }, [historyPageRows]);

  const businessTz = currentUser?.timeZone || 'America/Mexico_City';
  const fmtSentAt = useCallback((iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-MX', { timeZone: businessTz, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }, [businessTz]);
  const canalTxt = (c: string | null) => (c === 'email' ? 'Correo' : c === 'both' ? 'WhatsApp y correo' : c === 'whatsapp' ? 'WhatsApp' : '');

  // Exporta el RANGO completo cargado (no la página), con los campos elegidos en el modal.
  const handleHistoryExport = useCallback((selectedKeys: string[]) => {
    try {
      if (!historyRows.length) { toast.error('No hay datos para exportar'); return; }
      const fields = HISTORY_EXPORT_FIELDS.filter((f) => f.alwaysIncluded || selectedKeys.includes(f.key));
      const cell = (r: BirthdayHistoryRow, key: string): string => {
        switch (key) {
          case 'cliente': return r.visitorName || 'Sin nombre';
          case 'estado': return historialChipDe(r).txt.replace(/^[✓✗] /, '');
          case 'cumple': return r.bdayDate;
          case 'envio': return r.sentAt ? fmtSentAt(r.sentAt) : '';
          case 'canal': return r.sendStatus ? canalTxt(r.channel) : '';
          case 'telefono': return r.phoneNumber || '';
          case 'motivo': return r.sendStatus ? '' : (historialChipDe(r).tip || '');
          default: return '';
        }
      };
      const ws = XLSX.utils.aoa_to_sheet([fields.map((f) => f.header), ...historyRows.map((r) => fields.map((f) => cell(r, f.key)))]);
      ws['!cols'] = fields.map((f) => ({ wch: f.columnWidth || 16 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cumpleaños');
      XLSX.writeFile(wb, `historial_cumpleanos_${historyDays}d_${new Date().toLocaleDateString('sv')}.xlsx`);
      setShowHistoryExport(false);
      toast.success('Excel descargado');
    } catch (e) {
      console.error('[BirthdayPanel] export historial:', e);
      toast.error('Error al exportar Excel');
    }
  }, [historyRows, historyDays, fmtSentAt]);

  // ============================================================================
  // PAGINACIÓN LOCAL (sobre "próximos")
  // ============================================================================
  useEffect(() => {
    if (contentTopRef.current) {
      contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  const totalPages = Math.ceil(upcomingBirthdays.length / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const handleNextPage = useCallback(() => {
    if (hasNextPage && !loading && !pageChanging) {
      setPageChanging(true);
      setCurrentPage(prev => prev + 1);
      setTimeout(() => setPageChanging(false), 300);
    }
  }, [hasNextPage, loading, pageChanging]);

  const handlePrevPage = useCallback(() => {
    if (hasPrevPage && !loading && !pageChanging) {
      setPageChanging(true);
      setCurrentPage(prev => prev - 1);
      setTimeout(() => setPageChanging(false), 300);
    }
  }, [hasPrevPage, loading, pageChanging]);

  const paginatedUpcomingBirthdays = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return upcomingBirthdays.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [upcomingBirthdays, currentPage]);

  const groupedPaginatedBirthdays = useMemo<DailyUpcomingBirthdays[]>(() => {
    if (!paginatedUpcomingBirthdays.length) return [];
    const groups: Record<string, { birthdays: BirthdayContact[]; representative: BirthdayContact }> = {};
    paginatedUpcomingBirthdays.forEach(visitor => {
      if (visitor.birthMonth !== null && visitor.birthDay !== null) {
        const dateKey = `${visitor.birthMonth.toString().padStart(2, '0')}-${visitor.birthDay.toString().padStart(2, '0')}`;
        if (!groups[dateKey]) {
          groups[dateKey] = { birthdays: [], representative: visitor };
        }
        groups[dateKey].birthdays.push(visitor);
      }
    });
    return Object.entries(groups).map(([dateKey, groupData]) => ({
      dateKey,
      displayDate: formatBirthday(groupData.representative.birthDay, groupData.representative.birthMonth),
      birthdays: groupData.birthdays,
      count: groupData.birthdays.length,
    }));
  }, [paginatedUpcomingBirthdays]);


  // ============================================================================
  // MARKETING OPT-OUT (server-side via clientsApi)
  // ============================================================================
  const handleConfirmOptOut = async () => {
    if (!optOutCandidate || optingOutClientId) return;

    const visitor = optOutCandidate;
    setOptOutCandidate(null);
    setOptingOutClientId(visitor.id);

    const optOutPromise = (async () => {
      await clientsApi.setMarketingOptOut(visitor.id, true);
      setTodaysBirthdays(prev => prev.filter(b => b.id !== visitor.id));
      setUpcomingBirthdays(prev => prev.filter(b => b.id !== visitor.id));
    })();

    toast.promise(optOutPromise, {
      loading: 'Dando de baja…',
      success: `${visitor.visitorName || 'Cliente'} dado de baja de marketing`,
      error: 'Error al dar de baja',
    });

    try {
      await optOutPromise;
    } catch (err) {
      console.error('Error opt-out:', err);
    } finally {
      setOptingOutClientId(null);
    }
  };

  // ============================================================================
  // MODAL (Portal)
  // ============================================================================
  const OptOutModal = () => {
    if (!optOutCandidate) return null;

    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        aria-labelledby="optout-modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-overlay fixed inset-0 bg-slate-900/20 backdrop-blur-sm"
          onClick={() => setOptOutCandidate(null)}
        ></div>

        <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-sm modal-panel border border-slate-100 dark:border-line overflow-hidden">
          <div className="px-6 pt-6 flex justify-between items-start">
            <h3 className="text-lg font-bold text-content">Dar de baja</h3>
            <button
              onClick={() => setOptOutCandidate(null)}
              className="text-content-3 hover:text-content-2 p-1 hover:bg-canvas rounded-full transition-colors -mr-2 -mt-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 pb-6 pt-2">
            <p className="text-sm text-content-3 mb-6">
              ¿Dar de baja permanentemente a <strong className="text-content">{optOutCandidate.visitorName || optOutCandidate.phoneNumber}</strong> de todos los mensajes de marketing?
            </p>

            <div className="space-y-3">
              <button
                onClick={handleConfirmOptOut}
                className="w-full p-4 border border-red-100 dark:border-red-500/30 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-200 text-left flex items-center transition-all group shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                <div className="bg-surface border border-red-50 dark:border-red-500/20 p-2.5 rounded-full mr-4 text-red-400 group-hover:text-red-600 dark:text-red-400 group-hover:border-red-100 transition-colors shadow-sm">
                  <UserMinus className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-content group-hover:text-red-700 dark:text-red-300">Confirmar baja</p>
                  <p className="text-xs text-content-3 mt-0.5">No recibirá felicitaciones ni campañas.</p>
                </div>
              </button>
            </div>
          </div>

          <div className="px-6 py-3 bg-surface-2/50 border-t border-line flex justify-center">
            <button
              onClick={() => setOptOutCandidate(null)}
              className="text-sm text-content-3 hover:text-content-2 font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // ============================================================================
  // RENDER: Editor inline de template
  // ============================================================================
  const renderTemplateEditor = () => {
    if (!isEditingTemplate) return null;
    return (
      <div className="bg-surface shadow-md rounded-lg p-4 sm:p-5 border-l-4 border-blue-500 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-content">Personalizar mensaje de felicitación</h3>
          <button
            onClick={() => setIsEditingTemplate(false)}
            className="text-content-3 hover:text-content-2 p-1 hover:bg-canvas rounded-full transition-colors"
            aria-label="Cerrar editor"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <textarea
          value={templateDraft}
          onChange={(e) => setTemplateDraft(e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-line bg-canvas text-content text-sm p-3 focus:outline-none focus:ring-2 focus:ring-accent resize-y"
          placeholder={DEFAULT_BIRTHDAY_MESSAGE}
        />

        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-content-3">
          <span>Variables:</span>
          {['@nombre', '@negocio', '@puntos'].map(ph => (
            <code
              key={ph}
              className="px-1.5 py-0.5 rounded bg-surface-2 border border-line text-content-2 font-mono"
            >
              {ph}
            </code>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={() => {
              setTemplateDraft(settings?.template || DEFAULT_BIRTHDAY_MESSAGE);
              setIsEditingTemplate(false);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 border border-line transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveTemplate}
            disabled={savingTemplate}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-accent hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {savingTemplate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: Sección de envío automático (Premium + WhatsApp conectado)
  // ============================================================================
  const renderAutoSendSection = () => {
    // Plan único PRO: el envío automático de cumpleaños (antes 'premium') va incluido en PRO.
    if (!hasProAccess) return null;
    if (!canManage) return null;   // configuración: solo quien administra el módulo

    // Cargando estado de config o de la conexión WhatsApp
    if (autoLoading || waConfigured === null) {
      return (
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:px-6 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-content-3 mr-2" />
            <span className="text-sm text-content-3">Verificando configuración automática…</span>
          </div>
        </div>
      );
    }

    // WhatsApp no aprovisionado o no conectado → aviso de vincular
    if (!waConfigured || !waConnected) {
      return (
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-2 bg-canvas rounded-lg mr-3">
                  <WifiOff className="h-5 w-5 text-content-3" />
                </div>
                <div>
                  <p className="text-sm font-medium text-content">Envío automático de cumpleaños</p>
                  <p className="text-xs text-content-3">Vincula WhatsApp para habilitar el envío automático diario</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard/whatsapp')}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 transition-colors"
              >
                <Wifi className="h-3.5 w-3.5 mr-1.5" />
                Vincular WhatsApp
              </button>
            </div>
          </div>
        </div>
      );
    }

    // WhatsApp conectado — toggles
    return (
      <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
        {/* Toggle principal: Envío automático */}
        <div className="px-4 sm:px-6 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-2 rounded-lg mr-3 transition-colors ${autoEnabled ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-canvas'}`}>
                <Zap className={`h-5 w-5 transition-colors ${autoEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-content-3'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-content">Envío automático de cumpleaños</p>
                <p className="text-xs text-content-3">
                  {autoEnabled
                    ? 'Tus clientes reciben felicitación automática cada mañana'
                    : 'Activar para enviar felicitaciones automáticas diariamente'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleAutoEnabled}
              disabled={autoSaving}
              className="flex-shrink-0 ml-4 disabled:opacity-50"
              aria-label={autoEnabled ? 'Desactivar envío automático' : 'Activar envío automático'}
            >
              {autoSaving ? (
                <BrandLoader size={36} />
              ) : autoEnabled ? (
                <ToggleRight className="h-8 w-8 text-emerald-500 transition-colors" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-content-3 transition-colors" />
              )}
            </button>
          </div>
        </div>

        {/* Toggle secundario: Resumen diario (solo si auto está activo) */}
        {autoEnabled && (
          <div className="px-4 sm:px-6 py-3 border-b border-line bg-surface-2/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bell className={`h-4 w-4 mr-2.5 transition-colors ${notifyOwner ? 'text-blue-500' : 'text-content-3'}`} />
                <div>
                  <p className="text-sm text-content-2">Recibir resumen diario</p>
                  <p className="text-xs text-content-3">Te enviamos un WhatsApp con el resumen de felicitaciones del día</p>
                </div>
              </div>
              <button
                onClick={handleToggleNotifyOwner}
                disabled={autoSaving}
                className="flex-shrink-0 ml-4 disabled:opacity-50"
                aria-label={notifyOwner ? 'Desactivar resumen' : 'Activar resumen'}
              >
                {autoSaving ? (
                  <Loader2 className="h-6 w-6 text-content-3 animate-spin" />
                ) : notifyOwner ? (
                  <ToggleRight className="h-6 w-6 text-blue-500 transition-colors" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-content-3 transition-colors" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Regalo de puntos (solo si el automático está activo) */}
        {autoEnabled && (
          <div className="px-4 sm:px-6 py-3 border-b border-line bg-surface-2/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Gift className={`h-4 w-4 mr-2.5 transition-colors ${giftEnabled ? 'text-amber-500' : 'text-content-3'}`} />
                <div>
                  <p className="text-sm text-content-2">Regalarle puntos en su cumpleaños</p>
                  <p className="text-xs text-content-3">
                    {giftEnabled
                      ? `Cada cumpleañero recibe ${settings?.giftPoints ?? 0} puntos, una sola vez al año`
                      : 'Se le abonan solos el día de su cumpleaños, antes de mandarle el mensaje'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleGift}
                disabled={savingGift || (!giftEnabled && !(Number(giftPointsDraft) > 0))}
                className="flex-shrink-0 ml-4 disabled:opacity-50"
                aria-label={giftEnabled ? 'Desactivar regalo de puntos' : 'Activar regalo de puntos'}
              >
                {savingGift ? (
                  <Loader2 className="h-6 w-6 text-content-3 animate-spin" />
                ) : giftEnabled ? (
                  <ToggleRight className="h-6 w-6 text-amber-500 transition-colors" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-content-3 transition-colors" />
                )}
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2 pl-6">
              <label htmlFor="gift-points" className="text-xs text-content-3">Puntos a regalar</label>
              <input
                id="gift-points"
                type="number"
                min={1}
                max={500}
                value={giftPointsDraft}
                onChange={(e) => setGiftPointsDraft(e.target.value)}
                onBlur={handleSaveGiftPoints}
                disabled={savingGift}
                className="w-20 rounded-lg border border-line bg-surface px-2 py-1 text-sm text-content-1 disabled:opacity-50"
              />
              <span className="text-xs text-content-3">máx. 500</span>
            </div>

            {giftEnabled && (
              <div className="mt-3 flex items-start text-xs text-content-3">
                <AlertTriangle className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-amber-500" />
                <p>
                  Son puntos de tu programa: se le suman al saldo del cliente aunque no venga.
                  Escribe <span className="font-mono text-content-2">@regalo</span> en tu mensaje para
                  decirle cuántos le diste, y <span className="font-mono text-content-2">@puntos</span> para
                  su saldo ya con el regalo incluido.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info del horario */}
        {autoEnabled && (
          <div className="px-4 sm:px-6 py-3">
            <div className="flex items-start text-xs text-content-3">
              <Clock className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-content-3" />
              <p>
                Las felicitaciones se envían automáticamente por la mañana usando el mensaje
                personalizado de este panel. Se envían de forma escalonada para respetar los límites de WhatsApp.
              </p>
            </div>
          </div>
        )}

        {/* Imagen de felicitación: se adjunta a cada mensaje de cumpleaños por WhatsApp */}
        <div className="px-4 sm:px-6 py-4 border-t border-line">
          <div className="flex items-center mb-3">
            <ImageIcon className="h-4 w-4 mr-2 text-content-3" />
            <div>
              <p className="text-sm font-medium text-content">Imagen de felicitación</p>
              <p className="text-xs text-content-3">Se adjunta a cada mensaje de cumpleaños enviado por WhatsApp</p>
            </div>
          </div>
          {settings?.imageUrl && (
            <div className="flex items-center gap-3 mb-3">
              <img
                src={settings.imageUrl}
                alt="Imagen de cumpleaños"
                className="h-16 w-16 rounded-lg object-cover border border-line"
              />
              <div className="text-xs">
                <p className="text-emerald-600 dark:text-emerald-400 font-medium">✓ Se enviará como imagen en los mensajes de WhatsApp</p>
                <button
                  onClick={() => handleSetImage('')}
                  className="text-red-500 hover:underline mt-1"
                >
                  Quitar imagen
                </button>
              </div>
            </div>
          )}
          <ImageUpload
            initialImageUrl={settings?.imageUrl || ''}
            onImageChange={handleSetImage}
            title="Imagen de felicitación"
            description="Sube o arrastra una imagen, o pega una URL"
            accept="image/jpeg,image/png,image/webp"
          />
        </div>
      </div>
    );
  };

  // --- Historial (tarjeta) ---
  const renderHistorySection = () => (
    <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${isEditingTemplate ? '0.6s' : '0.5s'}` }}>
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-500/10 dark:to-gray-500/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-medium text-content flex items-center">
              <History className="h-5 w-5 mr-2 text-content-2" /> Historial de felicitaciones
            </h3>
            {!historyLoading && (
              <p className="mt-1 text-sm text-content-2">
                <span className="font-semibold text-content">{historyTotals.enviadas}</span> enviada{historyTotals.enviadas === 1 ? '' : 's'}
                {' · '}
                <span className="font-semibold text-content">{historyTotals.sinEnviar}</span> sin enviar
                {historyTotals.sinIntento > 0 && (
                  <>{' · '}<span className="font-semibold text-content">{historyTotals.sinIntento}</span> sin intento</>
                )}
                {' '}en los últimos {historyDays} días
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ExportExcelButton
              onExport={() => setShowHistoryExport(true)}
              disabled={historyLoading || historyRows.length === 0}
              filename={`historial_cumpleanos_${historyDays}d.xlsx`}
            />
            <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Rango del historial">
              {HISTORY_RANGES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setHistoryDays(d)}
                  disabled={historyLoading}
                  className={`px-3 py-1.5 text-xs font-medium border border-line first:rounded-l-md last:rounded-r-md -ml-px first:ml-0 transition-colors disabled:opacity-60 ${
                    historyDays === d
                      ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 z-10'
                      : 'bg-surface text-content-2 hover:bg-surface-2'
                  }`}
                >
                  {d} días
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6">
        {historyLoading && historyRows.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <BrandLoader size={36} />
            <span className="ml-2">Cargando…</span>
          </div>
        ) : historyRows.length === 0 ? (
          <div className="text-center py-8 animate-fade-in-up">
            <Cake className="h-12 w-12 text-content-3 mx-auto mb-3" />
            <p className="text-content-3">Aún no hay cumpleaños en los últimos {historyDays} días.</p>
          </div>
        ) : (
          // Al cambiar de rango la tabla anterior se queda (atenuada) hasta que llega la nueva:
          // si se vaciara, la página se acorta y el navegador «sube» al usuario sin querer.
          <div className={`transition-opacity ${historyLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Mobile */}
            <div className="md:hidden space-y-6">
              {historyGroups.map((group) => (
                <div key={group.dateKey}>
                  <div className="flex items-center mb-2">
                    <h4 className="text-base font-medium text-content">{group.displayDate}</h4>
                    <div className="ml-2 px-2 py-0.5 bg-surface-2 text-content-2 text-xs font-medium rounded-full flex items-center">
                      <Users className="h-3 w-3 mr-1" />{group.rows.length}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {group.rows.map((r) => (
                      <div key={r.id} className="border border-line rounded-lg p-3 hover:bg-surface-2 transition-all duration-150">
                        <ClientContactInfo clientId={r.id} clientName={r.visitorName} phoneNumber={r.phoneNumber} countryCode={r.countryCode} iconSize="sm" />
                        <HistorialChip row={r} />
                        {r.sentAt && (
                          <div className="text-xs text-content-3 mt-1">{fmtSentAt(r.sentAt)} · {canalTxt(r.channel)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop */}
            <div className="hidden md:block space-y-6">
              {historyGroups.map((group) => (
                <div key={group.dateKey}>
                  <div className="flex items-center mb-3">
                    <h3 className="text-lg font-medium text-content">{group.displayDate}</h3>
                    <div className="ml-3 px-2 py-1 bg-surface-2 text-content-2 text-xs font-medium rounded-full flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      {group.rows.length} cumpleaños
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-line rounded-lg">
                    <table className="min-w-full divide-y divide-line">
                      <thead className="bg-surface-2">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Envío</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-line">
                        {group.rows.map((r) => (
                          <tr key={r.id} className="hover:bg-surface-2 transition-all duration-150">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <ClientContactInfo clientId={r.id} clientName={r.visitorName} phoneNumber={r.phoneNumber} countryCode={r.countryCode} showName={true} nameAsLink={true} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-content-2">
                              {r.sentAt ? (
                                <>
                                  <div>{fmtSentAt(r.sentAt)}</div>
                                  <div className="text-xs text-content-3">{canalTxt(r.channel)}</div>
                                </>
                              ) : (
                                <span className="text-content-3">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <HistorialChip row={r} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            {historyRows.length > HISTORY_PER_PAGE && (
              <PaginationControls
                currentPage={historyPage}
                isLoading={historyLoading}
                onPrevPage={() => setHistoryPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                canGoPrev={historyPage > 1}
                canGoNext={historyPage < historyTotalPages}
                itemsPerPage={HISTORY_PER_PAGE}
                countOnPage={historyPageRows.length}
                totalItems={historyRows.length}
              />
            )}
          </div>
        )}
      </div>

      <ExportModal
        isOpen={showHistoryExport}
        onClose={() => setShowHistoryExport(false)}
        onExport={handleHistoryExport}
        fields={HISTORY_EXPORT_FIELDS}
        title="Exportar historial de cumpleaños"
        description={`Se exportan los ${historyRows.length} cumpleaños de los últimos ${historyDays} días. Elige las columnas.`}
        totalRecords={historyRows.length}
      />
    </div>
  );

  // --- Render Paywall ---
  if (showPaywall) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Cake className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-pink-500" /> Cumpleaños de Clientes
            </h1>
            <p className="mt-1 text-sm text-content-2">Visualiza los cumpleaños para enviar felicitaciones</p>
          </div>
          <ProLocked feature="birthday-panel" />
        </div>
      </Layout>
    );
  }

  // --- Main Render ---
  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div ref={contentTopRef} id="content-top"></div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Cake className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-pink-500" /> Cumpleaños de Clientes
            </h1>
            <p className="mt-1 text-sm text-content-2">Visualiza los cumpleaños para enviar felicitaciones</p>
          </div>
          <div className="flex items-center gap-2">
            {canManage && <button
              onClick={() => {
                setTemplateDraft(settings?.template || DEFAULT_BIRTHDAY_MESSAGE);
                setIsEditingTemplate(prev => !prev);
              }}
              className="inline-flex items-center px-4 py-2 border border-line shadow-sm text-sm rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
            >
              <Settings className="h-4 w-4 mr-2" /> Personalizar Mensaje
            </button>}
          </div>
        </div>

        {/* ── Sección de envío automático (Premium) ── */}
        {renderAutoSendSection()}

        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border-l-4 border-red-400 p-4 rounded animate-fade-in-up">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* ── Editor inline de template ── */}
        {renderTemplateEditor()}

        {/* Pestañas: Próximos (Hoy + 30 días) | Historial */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${isEditingTemplate ? '0.3s' : '0.2s'}` }}>
          <div className="border-b border-line">
            <nav className="flex -mb-px" aria-label="Secciones de cumpleaños">
              <button
                type="button"
                onClick={() => setActiveTab('proximos')}
                className={`w-1/2 py-3 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center gap-2 ${
                  activeTab === 'proximos'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <Gift className="h-4 w-4" />
                <span>Próximos</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('historial')}
                className={`w-1/2 py-3 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center gap-2 ${
                  activeTab === 'historial'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-3 hover:text-content-2 hover:border-line'
                }`}
              >
                <History className="h-4 w-4" />
                <span>Historial</span>
                {!historyLoading && historyTotals.sinEnviar > 0 && (
                  <span
                    title={`${historyTotals.sinEnviar} sin enviar`}
                    className="ml-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  >
                    {historyTotals.sinEnviar}<span className="hidden sm:inline"> sin enviar</span>
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {activeTab === 'proximos' && (
          <>
        {/* Today's Birthdays */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${isEditingTemplate ? '0.3s' : '0.2s'}` }}>
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-500/10 dark:to-purple-500/10">
            <h3 className="text-lg font-medium text-content flex items-center">
              <Gift className="h-5 w-5 mr-2 text-pink-500" /> Cumpleaños de Hoy ({localDay} de {currentMonthName})
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {loading && !pageChanging ? (
              <div className="flex justify-center items-center py-12">
                <BrandLoader size={36} />
                <span className="ml-2">Cargando…</span>
              </div>
            ) : todaysBirthdays.length > 0 ? (
              <div className="space-y-4">
                {todaysBirthdays.map((visitor, index) => (
                  <div key={visitor.id} className="border-l-4 border-pink-400 bg-pink-50 dark:bg-pink-500/10 p-4 rounded-md shadow-sm hover:shadow-md transition-shadow animate-fade-in-up" style={{ animationDelay: `${0.2 + index * 0.05}s` }}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <div>
                        <ClientContactInfo clientId={visitor.id} clientName={visitor.visitorName} phoneNumber={visitor.phoneNumber} countryCode={visitor.countryCode} />
                        <div className="text-xs text-content-3 mt-1 flex items-center">
                          {loyaltyMode === 'cashback' ? (
                            <>
                              <DollarSign className="h-3 w-3 mr-1 text-green-600 dark:text-green-400" />
                              <span className="text-green-700 dark:text-green-300 font-medium">${visitor.points.toFixed(2)}</span>
                            </>
                          ) : (
                            <>
                              <Star className="h-3 w-3 mr-1 text-yellow-500" />
                              <span>{Math.floor(visitor.points)} pts</span>
                            </>
                          )}
                        </div>
                        <EnvioChip visitor={visitor} autoEnabled={autoEnabled} />
                      </div>
                      <div className="mt-3 sm:mt-0 flex-shrink-0 flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setOptOutCandidate(visitor); }}
                          disabled={optingOutClientId === visitor.id}
                          className="p-1.5 text-content-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50"
                          title="Dar de baja de marketing"
                        >
                          {optingOutClientId === visitor.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <BellOff className="h-4 w-4" />
                          )}
                        </button>
                        {renderSendButton(visitor, 'md')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 animate-fade-in-up">
                <Cake className="h-12 w-12 text-content-3 mx-auto mb-3" />
                <p className="text-content-3">No hay cumpleaños registrados para hoy.</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Birthdays */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${isEditingTemplate ? '0.5s' : '0.4s'}` }}>
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
            <h3 className="text-lg font-medium text-content flex items-center">
              <Gift className="h-5 w-5 mr-2 text-blue-500" /> Próximos {DAYS_TO_FETCH} días
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {loading && !pageChanging ? (
              <div className="flex justify-center items-center py-12">
                <BrandLoader size={36} />
                <span className="ml-2">Cargando…</span>
              </div>
            ) : upcomingBirthdays.length === 0 ? (
              <div className="text-center py-8 animate-fade-in-up">
                <Cake className="h-12 w-12 text-content-3 mx-auto mb-3" />
                <p className="text-content-3">No hay más cumpleaños en los próximos {DAYS_TO_FETCH} días.</p>
              </div>
            ) : (
              <>
                {/* Mobile */}
                <div className="md:hidden space-y-6">
                  {groupedPaginatedBirthdays.map((group, groupIndex) => (
                    <div key={group.dateKey} className="animate-fade-in-up" style={{ animationDelay: `${0.4 + groupIndex * 0.1}s` }}>
                      <div className="flex items-center mb-2">
                        <h4 className="text-base font-medium text-content">{group.displayDate}</h4>
                        <div className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                          <Users className="h-3 w-3 mr-1" />{group.count}
                        </div>
                      </div>
                      <div className="space-y-3">
                        {group.birthdays.map((visitor) => (
                          <div key={visitor.id} className="border rounded-lg p-3 hover:bg-surface-2 transition-all duration-150">
                            <div className="flex justify-between items-start gap-3">
                              <div>
                                <ClientContactInfo clientId={visitor.id} clientName={visitor.visitorName} phoneNumber={visitor.phoneNumber} countryCode={visitor.countryCode} iconSize="sm" />
                                <EnvioChip visitor={visitor} autoEnabled={autoEnabled} />
                                <div className="text-xs text-content-3 mt-1 flex items-center">
                                  {loyaltyMode === 'cashback' ? (
                                    <>
                                      <DollarSign className="h-3 w-3 mr-1 text-green-600 dark:text-green-400" />
                                      <span className="text-green-700 dark:text-green-300 font-medium">${visitor.points.toFixed(2)}</span>
                                    </>
                                  ) : (
                                    <>
                                      <Star className="h-3 w-3 mr-1 text-yellow-500" />
                                      <span>{Math.floor(visitor.points)} pts</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-line flex justify-end items-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setOptOutCandidate(visitor); }}
                                disabled={optingOutClientId === visitor.id}
                                className="p-1.5 text-content-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50"
                                title="Dar de baja de marketing"
                              >
                                {optingOutClientId === visitor.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <BellOff className="h-4 w-4" />
                                )}
                              </button>
                              {renderSendButton(visitor, 'sm')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop */}
                <div className="hidden md:block space-y-8">
                  {groupedPaginatedBirthdays.map((group, groupIndex) => (
                    <div key={group.dateKey} className="animate-fade-in-up" style={{ animationDelay: `${0.4 + groupIndex * 0.1}s` }}>
                      <div className="flex items-center mb-3">
                        <h3 className="text-lg font-medium text-content">{group.displayDate}</h3>
                        <div className="ml-3 px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {group.count} cumpleaños
                        </div>
                      </div>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-line">
                          <thead className="bg-surface-2">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">
                                {loyaltyMode === 'cashback' ? 'Saldo' : 'Puntos'}
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">Acción</th>
                            </tr>
                          </thead>
                          <tbody className="bg-surface divide-y divide-line">
                            {group.birthdays.map((visitor) => (
                              <tr key={visitor.id} className="hover:bg-surface-2 transition-all duration-150">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <ClientContactInfo clientId={visitor.id} clientName={visitor.visitorName} phoneNumber={visitor.phoneNumber} countryCode={visitor.countryCode} showName={true} nameAsLink={true} />
                                  <EnvioChip visitor={visitor} autoEnabled={autoEnabled} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                  <div className="inline-flex items-center justify-center text-content-2">
                                    {loyaltyMode === 'cashback' ? (
                                      <span className="flex items-center text-green-700 dark:text-green-300 font-medium">
                                        <DollarSign className="h-3 w-3 mr-0.5" />
                                        {visitor.points.toFixed(2)}
                                      </span>
                                    ) : (
                                      <span className="flex items-center">
                                        <Star className="h-3 w-3 mr-1 text-yellow-500" />
                                        {Math.floor(visitor.points)} pts
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <div className="flex justify-center items-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setOptOutCandidate(visitor); }}
                                      disabled={optingOutClientId === visitor.id}
                                      className="p-1.5 text-content-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50"
                                      title="Dar de baja de marketing"
                                    >
                                      {optingOutClientId === visitor.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <BellOff className="h-4 w-4" />
                                      )}
                                    </button>
                                    {renderSendButton(visitor, 'sm')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>

                {upcomingBirthdays.length > ITEMS_PER_PAGE && (
                  <PaginationControls
                    currentPage={currentPage}
                    isLoading={loading || pageChanging}
                    onPrevPage={handlePrevPage}
                    onNextPage={handleNextPage}
                    canGoPrev={hasPrevPage && !pageChanging}
                    canGoNext={hasNextPage && !pageChanging}
                    itemsPerPage={ITEMS_PER_PAGE}
                    countOnPage={paginatedUpcomingBirthdays.length}
                    totalItems={upcomingBirthdays.length}
                  />
                )}
              </>
            )}
          </div>
        </div>

          </>
        )}

        {/* ── Historial (pestaña) ── */}
        {activeTab === 'historial' && renderHistorySection()}
      </div>

      {pageChanging && (
        <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
          <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
          <span className="text-sm font-medium text-content-2">Cambiando página…</span>
        </div>
      )}

      <OptOutModal />
    </Layout>
  );
};

export default BirthdayPanel;
